import { useCallback, useMemo } from 'react';

import { useHistory, useLocation } from 'react-router-dom';

import {
  DEFAULT_QA_STATUS_FILTER,
  DEFAULT_SORT_COLUMN,
  isQaStatusFilter,
  QaStatusFilter,
  SORTABLE_COLUMNS,
} from './reportsQaFilters';

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 25;

export interface ReportsQaParams {
  status: QaStatusFilter;
  search: string;
  page: number;
  perPage: number;
  sort: string;
  dir: 'asc' | 'desc';
}

const parsePositiveInt = (value: string | null, fallback: number): number => {
  const parsed = parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const parseReportsQaParams = (search: string): ReportsQaParams => {
  const query = new URLSearchParams(search);
  const status = query.get('status');
  const sort = query.get('sort');
  const perPage = parsePositiveInt(query.get('perPage'), DEFAULT_ROWS_PER_PAGE);

  return {
    status: isQaStatusFilter(status) ? status : DEFAULT_QA_STATUS_FILTER,
    search: query.get('q') ?? '',
    page: parsePositiveInt(query.get('page'), 1),
    perPage: ROWS_PER_PAGE_OPTIONS.includes(perPage)
      ? perPage
      : DEFAULT_ROWS_PER_PAGE,
    sort: sort && SORTABLE_COLUMNS[sort] ? sort : DEFAULT_SORT_COLUMN,
    dir: query.get('dir') === 'asc' ? 'asc' : 'desc',
  };
};

export const serializeReportsQaParams = (params: ReportsQaParams): string => {
  const query = new URLSearchParams();
  if (params.status !== DEFAULT_QA_STATUS_FILTER)
    query.set('status', params.status);
  if (params.search) query.set('q', params.search);
  if (params.page > 1) query.set('page', String(params.page));
  if (params.perPage !== DEFAULT_ROWS_PER_PAGE)
    query.set('perPage', String(params.perPage));
  if (params.sort !== DEFAULT_SORT_COLUMN) query.set('sort', params.sort);
  if (params.dir !== 'desc') query.set('dir', params.dir);
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

/**
 * Table state (filter, search, paging, sort) lives in the URL so it survives
 * opening a report preview and coming back, and can be shared as a link.
 */
const useReportsQaParams = (): [
  ReportsQaParams,
  (patch: Partial<ReportsQaParams>) => void,
] => {
  const location = useLocation();
  const history = useHistory();

  const params = useMemo(
    () => parseReportsQaParams(location.search),
    [location.search],
  );

  const setParams = useCallback(
    (patch: Partial<ReportsQaParams>) => {
      const next = { ...parseReportsQaParams(history.location.search), ...patch };
      history.replace({
        pathname: history.location.pathname,
        search: serializeReportsQaParams(next),
      });
    },
    [history],
  );

  return [params, setParams];
};

export default useReportsQaParams;
